import { Add, Delete } from "@mui/icons-material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { Box, Button, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import excel from "assets/icons/filetypes/excel.png";
import Announcement from "assets/images/Announcement.svg";
import {
  bulkDeleteServiceDescriptions,
  deleteServiceDescription,
  exportServiceDescriptions,
  getServiceDescriptions,
} from "api/services/serviceDescription";
import Loader from "components/Loader";
import SearchContainer from "components/SearchContainer";
import Table, { ColumnType } from "components/Table";
import sortHandler from "components/Table/SortHandler";
import { snack } from "components/toast";
import { useConfirm } from "context/ConfirmDialog";
import { SortDirection } from "data/constants";
import { TAXES } from "data/taxes";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ResType } from "types";
import { getCurrentFormattedDateTime } from "utils";
import { formattedAmount } from "utils/formattedAmount";
import { handleError } from "utils/handleError";
import AddServiceDescription from "views/settings/organization/ServiceDescription/AddServiceDescription";
import EditServiceDescription from "views/settings/organization/ServiceDescription/EditServiceDescription";
import ImportServiceDescriptions from "views/settings/organization/ServiceDescription/ImportServiceDescriptions";
import ServiceDescriptionAnnouncement from "views/settings/organization/ServiceDescription/ServiceDescriptionAnnouncement";

function ServiceDescription() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<boolean>(false);
  const [openImport, setOpenImport] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selected, setSelected] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const [sort, setSort] = useState<any>({});

  const { data, isLoading }: ResType = useQuery(
    [
      "service-descriptions",
      {
        limit: pageCount,
        offset: page * pageCount,
        search,
        sort,
      },
    ],
    getServiceDescriptions,
    {
      onSuccess: (res: any) => {
        setTotalCount(res?.data?.totalCount || 0);
      },
      onError: (err: any) => {
        if (!err?.error) {
          snack.error("An error occurred while fetching service descriptions");
        }
      },
    }
  );

  const { mutate: removeServiceDescription } = useMutation(deleteServiceDescription, {
    onSuccess: () => {
      snack.success("Service Description Deleted");
      queryClient.invalidateQueries("service-descriptions");
    },
    onError: (err: any) => {
      snack.error(handleError(err));
    },
  });

  const { mutate: removeSelectedServiceDescriptions } = useMutation(bulkDeleteServiceDescriptions, {
    onSuccess: () => {
      snack.success("Service Descriptions Deleted");
      setSelected([]);
      queryClient.invalidateQueries("service-descriptions");
    },
    onError: (err: any) => {
      snack.error(handleError(err));
    },
  });

  const { mutate: exportData, isLoading: exportLoading } = useMutation(exportServiceDescriptions, {
    onSuccess: (blob: Blob) => {
      const file = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const formattedDateTime = getCurrentFormattedDateTime();
      link.href = file;
      link.download = `ATOM - Service Fee List ${formattedDateTime}.xlsx`;
      link.click();
      snack.success("Exported successfully");
    },
    onError: (err: any) => {
      snack.error(handleError(err));
    },
  });

  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(0);
    setSelected([]);
  };

  const handleRemoveSelected = () => {
    setAnchorEl(null);
    confirm({
      msg: `Are you sure you want to delete ${selected.length} selected service description(s)?`,
      action: () => {
        removeSelectedServiceDescriptions({ ids: selected.map((item) => item.id) });
      },
    });
  };

  const handleUpdateSelected = () => {
    setAnchorEl(null);
    setOpenEdit(true);
  };

  const column: Array<ColumnType> = [
    {
      title: "Service Description",
      key: "description",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      render: (item) => (
        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
          {item?.description}
        </Typography>
      ),
    },
    {
      title: "HSN/SAC",
      key: "hsnSac",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
    },
    {
      title: "GST Rate",
      key: "gstRate",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      render: (item) => (
        <Typography variant="body2">
          {TAXES.find((tax) => tax.value === item?.gstRate)?.name || item?.gstRate}
        </Typography>
      ),
    },
    {
      title: "Service Fee",
      key: "serviceFee",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      render: (item) => (
        <Typography variant="body2">
          {item?.serviceFee !== null && item?.serviceFee !== undefined && item?.serviceFee !== ""
            ? formattedAmount(item.serviceFee)
            : "-"}
        </Typography>
      ),
    },
  ];
  const [columns, setColumns] = useState(column);

  const handleSort = (v: any) => {
    sortHandler({
      key: v.key,
      columns,
      sortState: sort,
      setSortState: setSort,
      setColumns,
    });
    setPage(0);
  };

  return (
    <Box m={2}>
      <Box display="flex" gap={3} justifyContent="space-between" alignItems="center" mb={2}>
        <SearchContainer
          value={search}
          debounced
          minWidth="400px"
          onChange={handleSearch}
          placeHolder="Search by Service Description | HSN/SAC"
        />
        <Box display="flex" gap={2} alignItems="center">
          {selected.length > 0 && (
            <Button
              onClick={(e) => setAnchorEl(e.currentTarget)}
              endIcon={<KeyboardArrowDownOutlinedIcon />}
              color="secondary"
              variant="outlined"
            >
              Actions
            </Button>
          )}
          <Button
            onClick={() => setOpen(true)}
            startIcon={<Add />}
            color="secondary"
            variant="outlined"
          >
            Add
          </Button>
          <Button
            onClick={() => setOpenImport(true)}
            color="secondary"
            variant="outlined"
          >
            Bulk Import
          </Button>
          <Tooltip title={exportLoading ? "Exporting..." : "Data Export to Excel"}>
            <img
              src={excel}
              alt="Export"
              style={{
                cursor: exportLoading ? "not-allowed" : "pointer",
                width: 28,
                height: 28,
                opacity: exportLoading ? 0.5 : 1,
              }}
              onClick={() => {
                if (exportLoading) return;
                exportData({ search });
              }}
            />
          </Tooltip>
          <Button
            onClick={() => setOpenInfo(true)}
            sx={{
              backgroundColor: "#fff",
              "&:hover": { backgroundColor: "#fff" },
              p: 0,
              minWidth: "auto",
            }}
          >
            <Tooltip title="Announcement">
              <img
                src={Announcement}
                alt="Announcement"
                style={{
                  cursor: "pointer",
                  width: 33,
                  height: 33,
                }}
              />
            </Tooltip>
          </Button>
        </Box>
      </Box>
      {isLoading ? (
        <Loader />
      ) : (
        <Table
          columns={columns}
          loading={isLoading}
          data={data?.data?.result || []}
          selection={{ selected, setSelected }}
          pagination={{ totalCount, page, setPage, pageCount, setPageCount }}
          sortHandler={handleSort}
        />
      )}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleUpdateSelected} disabled={selected.length !== 1}>
          Update
        </MenuItem>
        <MenuItem onClick={handleRemoveSelected} disabled={selected.length === 0}>
          Delete
        </MenuItem>
      </Menu>
      <AddServiceDescription open={open} setOpen={setOpen} />
      <ImportServiceDescriptions open={openImport} setOpen={setOpenImport} />
      <ServiceDescriptionAnnouncement open={openInfo} setOpen={setOpenInfo} />
      <EditServiceDescription
        open={openEdit}
        setOpen={setOpenEdit}
        data={selected[0]}
        onUpdated={() => setSelected([])}
      />
    </Box>
  );
}

export default ServiceDescription;
